'use client';

import { OTHERS_BUCKET_ID, type Breakdown } from '@/lib/transactions/utils';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { cn } from '@/lib/utils';
import { StackedBar, breakdownSegments } from './StackedBar';
import type { CategoryIndex } from './categoryIndex';

interface Props {
  breakdown: Breakdown;
  categoryIndex: CategoryIndex;
  selectedCategoryIds?: readonly string[];
  categoryFilterIdByCategoryId?: ReadonlyMap<string, string>;
  onSelectCategory?: (categoryId: string) => void;
  onSeeAll?: () => void;
}

export function CategoryBreakdownCard({
  breakdown,
  categoryIndex,
  selectedCategoryIds = [],
  categoryFilterIdByCategoryId,
  onSelectCategory,
  onSeeAll,
}: Props) {
  const formatCurrency = useFormatCurrency();

  if (breakdown.segments.length === 0) {
    return (
      <section className="rounded-3xl border border-dashed border-border-subtle bg-surface p-8 text-center text-sm text-text-secondary">
        Nenhuma categoria com movimento neste período.
      </section>
    );
  }

  const segments = breakdownSegments(breakdown, categoryIndex);
  const hasSelection = selectedCategoryIds.length > 0;
  const filterId = (id: string) => categoryFilterIdByCategoryId?.get(id) ?? id;
  const isSelected = (id: string) => selectedCategoryIds.length === 1 && selectedCategoryIds[0] === filterId(id);
  const isFilterable = (id: string) => id !== OTHERS_BUCKET_ID;
  const canOpenCategoryDrawer = (id: string) => id === OTHERS_BUCKET_ID && Boolean(onSeeAll);
  const handleSelectLegendCategory = (id: string) => {
    if (id === OTHERS_BUCKET_ID) {
      onSeeAll?.();
      return;
    }

    onSelectCategory?.(filterId(id));
  };

  return (
    <section className="rounded-3xl border border-border-subtle bg-surface p-4 shadow-sm sm:p-5">
      <header className="mb-3 flex items-baseline justify-between">
        <h2 className="font-mono text-2xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
          Por categoria
        </h2>
        <p className="font-mono text-xs font-medium text-amount-negative tabular-nums">
          {formatCurrency(-breakdown.total)}
        </p>
      </header>

      <StackedBar
        breakdown={breakdown}
        categoryIndex={categoryIndex}
        height={10}
        selectedCategoryIds={selectedCategoryIds}
        categoryFilterIdByCategoryId={categoryFilterIdByCategoryId}
        onSelectCategory={onSelectCategory}
      />

      <ul role="list" className="mt-3.5 grid grid-cols-2 gap-x-4 gap-y-2">
        {segments.map((s) => (
          <li key={s.id}>
            <CategoryLegendButton
              id={s.id}
              label={s.label}
              color={s.color}
              pct={s.pct}
              filterable={isFilterable(s.id) || canOpenCategoryDrawer(s.id)}
              selected={isSelected(s.id)}
              muted={hasSelection && !isSelected(s.id)}
              onSelectCategory={handleSelectLegendCategory}
            />
          </li>
        ))}
      </ul>

      {onSeeAll && (
        <div className="mt-3 border-t border-divider pt-3">
          <button
            type="button"
            onClick={onSeeAll}
            className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-sm font-medium text-primary hover:bg-primary/5"
          >
            <span>Ver todas as categorias</span>
            <span aria-hidden>→</span>
          </button>
        </div>
      )}
    </section>
  );
}

interface LegendButtonProps {
  id: string;
  label: string;
  color: string;
  pct: number;
  filterable: boolean;
  selected: boolean;
  muted: boolean;
  onSelectCategory?: (categoryId: string) => void;
}

function CategoryLegendButton({
  id,
  label,
  color,
  pct,
  filterable,
  selected,
  muted,
  onSelectCategory,
}: LegendButtonProps) {
  const content = (
    <>
      <span
        aria-hidden
        className="size-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">{label}</span>
      <p className="shrink-0 font-mono text-xs font-medium text-text-tertiary tabular-nums">
        {Math.round(pct)}%
      </p>
    </>
  );

  if (!filterable) {
    return (
      <div className={cn('flex min-h-10 items-center gap-2 rounded-xl px-2', muted && 'opacity-55')}>
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onSelectCategory?.(id)}
      className={cn(
        'flex min-h-10 w-full items-center gap-2 rounded-xl px-2 text-left transition',
        selected ? 'bg-primary/5 ring-1 ring-primary/25' : 'hover:bg-accent',
        muted && 'opacity-55',
      )}
    >
      {content}
    </button>
  );
}
